const en = {
  common: {
    appName: "NO VAX LOVE",
    slogan: "One membership. Everything included.",
    signIn: "Sign in",
    signUp: "Sign up",
    signOut: "Sign out",
    email: "Email",
    password: "Password",
    submit: "Continue",
    cancel: "Cancel",
    or: "or",
    joinCta: "Join NO VAX LOVE",
    discoverCta: "Discover how it works",
    loading: "Loading...",
    language: "Language",
  },
  nav: {
    home: "Home",
    features: "Community",
    philosophy: "Philosophy",
    faq: "FAQ",
    login: "Sign in",
    register: "Sign up",
  },
  landing: {
    hero: {
      overline: "A European community, for adults",
      title: "Find who shares your vision.",
      subtitle:
        "A dating community created for adults who share a particular worldview regarding COVID-19 vaccination and personal freedom. Everyone is welcome, including vaccinated people and anyone aligned with our values.",
    },
    what: {
      overline: "What it is",
      title: "An elegant way to meet.",
      body:
        "NO VAX LOVE is a sophisticated online space for adults looking to meet someone with genuine affinity. No aggression, no politics — only authenticity, respect and connection.",
    },
    why: {
      overline: "Why it's different",
      title: "Compatibility beyond the surface.",
      items: [
        { title: "Shared values", body: "People start from the same page, naturally." },
        { title: "A curated community", body: "A calm, elegant environment with identity checks." },
        { title: "No premium tiers", body: "One membership. Every feature included." },
        { title: "European design", body: "Refined, editorial, mobile-first, uncluttered." },
      ],
    },
    compatibility: {
      overline: "Compatibility",
      title: "The best connections come from values.",
      body:
        "The NO VAX LOVE system helps you discover people with authentic affinities: lifestyle, expectations and views on personal freedom.",
    },
    membership: {
      overline: "Membership",
      title: "One membership. Everything included.",
      body:
        "No hidden upsell. No locked features. With an active membership you get: unlimited likes, matching, advanced search, compatibility, messaging, photo sharing, voice messages, filters and discovery.",
      cta: "Join NO VAX LOVE",
    },
    trust: {
      overline: "Trust",
      title: "Verified identity, quietly.",
      body:
        "Manual profile checks, active moderation, and tools to report inappropriate behavior. Trust is at the center of the community.",
    },
    privacy: {
      overline: "Privacy",
      title: "Privacy by design.",
      body:
        "Your exact location is never shared — only city and approximate distance. Your personal data stays protected.",
    },
    how: {
      overline: "How it works",
      title: "Three simple steps.",
      steps: [
        { title: "Create your profile", body: "Share who you are with elegance and honesty." },
        { title: "Discover kindred people", body: "Browse profiles curated to your values." },
        { title: "Meet authentically", body: "Chat, video-call, and meet in real life when you feel ready." },
      ],
    },
    community: {
      overline: "Philosophy",
      title: "Open, adult, respectful.",
      body:
        "Everyone is welcome: vaccinated people, those who have changed their mind, and anyone aligned with the community's values. NO VAX LOVE does not discriminate.",
    },
    faq: {
      overline: "FAQ",
      title: "Frequently asked questions.",
      items: [
        {
          q: "Who can join NO VAX LOVE?",
          a: "Adults (18+) who feel aligned with the community's values. No one is excluded based on vaccination status.",
        },
        {
          q: "Do I need to prove my vaccination status?",
          a: "No. We never ask for proof of vaccination status or any medical data.",
        },
        {
          q: "Are there premium tiers?",
          a: "No. A single membership includes every feature on the platform.",
        },
        {
          q: "How do you handle privacy?",
          a: "Privacy by design: we display only city and approximate distance, never your exact location.",
        },
        {
          q: "Where is it available?",
          a: "NO VAX LOVE is designed for the European market, starting in Italian and English.",
        },
      ],
    },
    finalCta: {
      overline: "Start today",
      title: "Ready to meet people who share your vision?",
      body: "Quick, elegant and always discreet.",
      cta: "Join NO VAX LOVE",
    },
    footer: {
      about: "About",
      privacy: "Privacy",
      terms: "Terms",
      contact: "Contact",
      rights: "All rights reserved.",
    },
  },
  auth: {
    login: {
      title: "Welcome back.",
      subtitle: "Sign in to your NO VAX LOVE account.",
      submit: "Sign in",
      forgot: "Forgot password?",
      noAccount: "Don't have an account?",
      register: "Sign up",
    },
    register: {
      title: "Create your account.",
      subtitle: "It only takes a moment. Discreet and secure.",
      submit: "Create account",
      adult: "I confirm I am at least 18 years old.",
      terms: "By signing up you accept the Terms and Privacy Policy.",
      have: "Already have an account?",
      login: "Sign in",
      country: "Country",
      city: "City",
    },
    forgot: {
      title: "Reset your password.",
      subtitle: "We'll email you a reset link.",
      submit: "Send link",
      back: "Back to sign in",
      sent: "If the email is registered, you'll receive a link shortly.",
    },
    reset: {
      title: "Choose a new password.",
      subtitle: "Pick a secure password of at least 8 characters.",
      submit: "Update password",
      success: "Password updated. You can sign in now.",
    },
    verify: {
      title: "Verify your email.",
      subtitle: "Verifying your link...",
      success: "Email verified. Thank you!",
      failure: "Invalid or expired link.",
    },
    errors: {
      minPassword: "Password must be at least 8 characters.",
      mustBeAdult: "You must confirm you are at least 18 years old.",
    },
  },
  app: {
    nav: {
      discover: "Discover",
      matches: "Matches",
      likes: "Likes",
      messages: "Messages",
      profile: "My profile",
      notifications: "Notifications",
      settings: "Settings",
    },
    discovery: {
      title: "Discover",
      subtitle: "Meet people who share your interests, values and lifestyle.",
      filtersButton: "Filters",
      activeFilters: "active",
      like: "Like",
      pass: "Pass",
      undoLike: "Undo like",
      undoPass: "Undo",
      undoToast: "Undone",
      likeSent: "Like sent",
      passDone: "Profile skipped",
      actionError: "Something went wrong. Please try again.",
      sortLabel: "Sort by",
      sort: {
        recommended: "Recommended",
        most_compatible: "Most compatible",
        nearby: "Nearby",
        new_here: "New here",
        recently_active: "Recently active",
      },
      compatibleShort: "compatible",
      compatibleLong: "Compatibility",
      confidenceLearning: "Compatibility: still learning",
      confidenceLimited: "Compatibility based on limited information",
      whyCompatible: "Why you're compatible",
      sharedInterests: "You both like",
      sharedValues: "You both value",
      sharedTopValues: "Shared top values",
      sharedGoals: "You're both looking for",
      sharedCommunication: "Compatible communication",
      similarLifestyle: "Similar lifestyle",
      familyAligned: "Aligned family outlook",
      sameFaith: "Same spirituality",
      geoNearby: "Geographically close",
      sharedPerspective: "Shared community perspective",
      empty: "No profiles match your current filters.",
      broaden: "Broaden your search",
      loadingError: "We couldn't load new profiles right now. Please try again.",
      retry: "Retry",
      km: "km",
      verified: "Identity verified",
      loadMore: "Load more",
      back: "Back",
      openProfile: "Open profile",
      completeProfile: "Complete your profile",
      completeProfileBody: "To discover compatible people, add a few details to your profile first.",
      insufficientCompatData: "Not enough data for a precise estimate.",
      category: {
        exceptional: "Exceptional match",
        very_compatible: "Very compatible",
        strong: "Strong compatibility",
        good_potential: "Good potential",
        some_common_ground: "Some common ground",
        different: "Different profiles",
      },
      filters: {
        title: "Filters",
        reset: "Reset",
        apply: "Apply",
        age: "Age",
        ageMin: "Min",
        ageMax: "Max",
        distance: "Distance",
        anywhere: "Anywhere",
        countries: "Countries",
        cities: "Cities",
        countriesPlaceholder: "Add countries (comma separated)",
        citiesPlaceholder: "Add cities (comma separated)",
        interestedIn: "Interested in",
        relationshipGoals: "Looking for",
        verifiedOnly: "Verified identity only",
        interests: "Interests",
        interestsMatch: "Interest match",
        interestsAny: "Any",
        interestsAll: "All",
        values: "Values",
        lifestyle: "Lifestyle",
        smoking: "Smoking",
        alcohol: "Alcohol",
        pets: "Pets",
        diet: "Diet",
        family: "Family",
        children: "Children",
        wantsChildren: "Wants children",
        openToChildren: "Open to dating someone with children",
        religion: "Religion (optional)",
        community: "Community perspective (optional)",
        communityNote: "Optional filter. Off by default. It does NOT automatically exclude anyone.",
        enableCommunity: "Enable community filter",
      },
    },
    match: {
      itsAMatch: "It's a match!",
      subtitle: "You liked each other.",
      sendMessage: "Send a message",
      keepDiscovering: "Keep discovering",
      matchedOn: "Matched on",
      matchedRecently: "Matched recently",
      title: "My matches",
      subtitleList: "People you both showed interest in.",
      empty: "No matches yet.",
      emptyBody: "Keep discovering people — when you both like each other, they'll appear here.",
      detailTitle: "Match",
      messagingComingSoon: "Messaging coming soon",
      messagingBody: "You'll be able to chat directly with this person soon. In the meantime, check out their profile.",
      viewProfile: "View profile",
    },
    likes: {
      title: "Likes sent",
      subtitle: "People you've liked.",
      empty: "You haven't sent any likes yet.",
      isMatch: "Match!",
      waiting: "Waiting",
      openMatch: "Open match",
    },
    messaging: {
      inboxTitle: "Messages",
      inboxSubtitle: "Your conversations with matches.",
      inboxEmpty: "No conversations yet.",
      inboxEmptyBody: "Once you send a message to a match, it'll appear here.",
      placeholder: "Write a message...",
      send: "Send",
      charsLeft: "{n} characters left",
      you: "You",
      matched: "Match",
      delivered: "Delivered",
      readAt: "Read",
      rateLimited: "You've sent too many messages. Please wait a moment.",
      sendError: "Couldn't send your message. Try again.",
      loadingError: "Couldn't load messages.",
      newMessageNotif: "New message from {name}",
      today: "Today",
      yesterday: "Yesterday",
      photo: "Photo",
      voiceMessage: "Voice message",
      attachPhoto: "Attach photo",
      recordVoice: "Record voice message",
      startRecording: "Tap to start recording",
      recording: "Recording...",
      cancel: "Cancel",
      stop: "Stop",
      previewPhoto: "Preview",
      photoTooLarge: "That image is too large (max 10 MB).",
      voiceTooLarge: "That audio is too large (max 8 MB).",
      unsupportedType: "Unsupported file type.",
      micDenied: "Microphone permission denied. Enable it in your browser settings.",
      micUnavailable: "Microphone not available in this browser.",
      reactHeart: "Heart this message",
      removeReaction: "Remove heart",
    },
    moderation: {
      overflowMenu: "More",
      block: "Block",
      report: "Report",
      unblock: "Unblock",
      blockConfirmTitle: "Do you want to block this person?",
      blockConfirmBody: "You won't see each other, like each other, or exchange messages anymore. They won't be notified.",
      blockConfirmCta: "Block",
      blockDone: "Person blocked",
      unblockDone: "Person unblocked",
      cancel: "Cancel",
      reportTitle: "Report this person",
      reportBody: "Help us keep the community safe. Choose a reason.",
      reportReasonLabel: "Reason",
      reportDescLabel: "Description (optional)",
      reportDescPlaceholder: "Add details if you'd like.",
      reportSubmit: "Submit report",
      reportDone: "Report submitted. Thank you.",
      reportError: "Couldn't submit your report. Try again.",
      reportDuplicate: "You've already reported this person recently.",
      reasons: {
        harassment: "Harassment",
        spam: "Spam",
        fake_profile: "Fake profile",
        scam: "Scam",
        inappropriate_content: "Inappropriate content",
        other: "Other",
      },
      blockedListTitle: "Blocked users",
      blockedListEmpty: "You haven't blocked anyone.",
      blockedListSubtitle: "People you've blocked can't see you, message you, or get in touch with you.",
      blockedSince: "Blocked on {date}",
      unblockConfirmTitle: "Unblock this person?",
      unblockConfirmBody: "You'll be able to see each other in Discovery again, but previous Likes and Matches that were removed by the block will NOT be automatically restored.",
      unblockConfirmCta: "Unblock",
      loading: "Loading...",
      loadError: "Couldn't load the list.",
    },
    account: {
      section: "Account & privacy",
      privacyTitle: "Privacy & data",
      privacySubtitle: "Manage your personal data, download a copy, or request account deletion.",
      exportTitle: "Download my data",
      exportBody: "Download a JSON file with a copy of your personal data (profile, messages, matches, reports, preferences).",
      exportCta: "Download JSON",
      exportError: "Couldn't generate the export. Please try again.",
      deleteTitle: "Delete account",
      deleteSubtitle: "Permanently delete your account and personal data.",
      deleteCta: "Delete account",
      deletePageTitle: "Delete your account",
      deleteWarningTitle: "Before you continue",
      deleteWarningBullets: [
        "Your account will enter a 30-day grace period.",
        "During this time you won't be able to sign in normally and your profile won't be visible.",
        "You can cancel the deletion by signing back in with your email and password within 30 days.",
        "After 30 days your personal data (profile, photos, messages, preferences) will be permanently deleted or anonymized.",
        "Some records may be retained to meet legal, security, or audit obligations.",
      ],
      passwordConfirmLabel: "Confirm your password",
      typedConfirmLabel: "Type DELETE to confirm",
      typedConfirmValue: "DELETE",
      typedConfirmHint: "Type DELETE exactly (in uppercase).",
      deleteReasonLabel: "Reason (optional)",
      deleteReasonPlaceholder: "What's making you leave?",
      deleteSubmit: "Permanently delete",
      deleteScheduledTitle: "Deletion scheduled",
      deleteScheduledBody: "Your account will be deleted on {date}. You can cancel any time by signing in again.",
      cancelDeletion: "Cancel deletion",
      deletionError: "Couldn't complete the operation.",
      invalidConfirmation: "Confirmation word doesn't match.",
      invalidPassword: "Incorrect password.",
      recovery: {
        title: "Your account is scheduled for deletion",
        body: "You requested account deletion. If you've changed your mind, sign in with your email and password to cancel. After 30 days your data will be permanently removed.",
        reactivateCta: "Reactivate my account",
        emailLabel: "Email",
        passwordLabel: "Password",
        scheduledOn: "Scheduled deletion: {date}",
        success: "Account reactivated. You can now sign in.",
        invalidCredentials: "Invalid email or password.",
        notPendingDeletion: "This account is not scheduled for deletion.",
      },
    },
    legal: {
      privacyTitle: "Privacy Policy",
      termsTitle: "Terms of Service",
      cookiesTitle: "Cookie Policy",
      lastUpdated: "Last updated: {date}",
      placeholderBadge: "PLACEHOLDER — legal text pending professional review",
      draftNotice: "This is a technical draft. Final legal wording will be provided by a legal professional prior to public launch.",
      backHome: "Back to home",
      privacy: {
        intro: "NO VAX LOVE processes personal data to provide an online dating service to adults residing in Europe. This document describes what data we collect, why, and for how long.",
        sections: [
          { title: "1. Who we are", body: "[PLACEHOLDER — data controller, address, contact, DPO if applicable]" },
          { title: "2. Data we collect", body: "Account (email, hashed password, language), profile (display name, age, interests, photos, values, preferences), interactions (likes, matches, messages, blocks, reports), technical data (IP address, security logs), payment data (processed by Stripe — we never store card numbers)." },
          { title: "3. Legal bases", body: "Performance of the contract (GDPR art. 6.1.b) to deliver the service; consent (art. 6.1.a) for optional communications; legitimate interest (art. 6.1.f) for security and fraud prevention." },
          { title: "4. How we use your data", body: "To operate the platform, surface compatible profiles, keep the community safe, respond to reports, process payments, and comply with legal obligations." },
          { title: "5. Retention", body: "[PLACEHOLDER — final retention schedule. Current draft: profile data deleted or anonymized within 30 days of a deletion request; security logs kept up to 12 months; tax/financial records retained per applicable tax law.]" },
          { title: "6. Sharing with third parties", body: "Essential technical providers: Stripe (payments), object storage vendor for photos and attachments, transactional email provider. We do not sell your data." },
          { title: "7. Extra-EU transfers", body: "[PLACEHOLDER — list any transfers and applicable Standard Contractual Clauses.]" },
          { title: "8. Your rights", body: "You may exercise your rights under GDPR articles 15-22: access, rectification, erasure, portability, objection, restriction. Inside the app you can download an export of your data and request account deletion." },
          { title: "9. Contact", body: "[PLACEHOLDER — dedicated privacy email and/or contact form.]" },
        ],
      },
      terms: {
        intro: "By using NO VAX LOVE you accept the conditions described in this document. If you do not accept them, please do not use the service.",
        sections: [
          { title: "1. Who can join", body: "NO VAX LOVE is restricted to adults aged 18 or older. We do not allow minors to register." },
          { title: "2. Community behavior", body: "Illegal content, harassment, spam, fake profiles, scams, non-consensual sexual content and aggressive behavior are forbidden. Users who break the rules may be suspended or removed." },
          { title: "3. User content", body: "You are responsible for the content you publish (photos, texts, voice messages). You grant NO VAX LOVE a non-exclusive licence, limited to operating the service, to host and display such content." },
          { title: "4. Membership and payments", body: "[PLACEHOLDER — economic terms, auto-renewal, right of withdrawal under EU distance-contract law.]" },
          { title: "5. Suspension and termination", body: "We reserve the right to suspend or remove accounts that breach the terms. You can delete your account at any time from the settings area." },
          { title: "6. Limitation of liability", body: "[PLACEHOLDER — standard limitation clauses within the limits allowed by EU law.]" },
          { title: "7. Governing law and jurisdiction", body: "[PLACEHOLDER — applicable law and competent court for EU consumer users.]" },
          { title: "8. Changes to the terms", body: "Material updates will be communicated with reasonable notice by email or in-app notification." },
        ],
      },
      cookies: {
        intro: "We use strictly-necessary technical cookies (authentication, language preference). We do not use third-party advertising trackers.",
        sections: [
          { title: "Strictly-necessary cookies", body: "Required for authentication (access_token, refresh_token) and to remember the selected language. These do not require consent under EU law." },
          { title: "Analytics or marketing cookies", body: "[PLACEHOLDER — NOT currently used. If introduced later, we will ask for explicit consent and provide a granular management panel.]" },
          { title: "How to manage cookies", body: "You can manage and delete cookies from your browser settings. Disabling required cookies may prevent access to the service." },
        ],
        banner: {
          message: "We use only strictly-necessary technical cookies to run the service. See our cookie policy for details.",
          accept: "Got it",
          moreInfo: "Cookie policy",
        },
      },
    },
    admin: {
      nav: {
        moderation: "Moderation",
        auditLog: "Audit log",
        billing: "Pricing",
      },
      billing: {
        title: "Pricing configuration",
        subtitle: "Manage promotional phase, prices and plan availability.",
        phaseLabel: "Current phase",
        phase: {
          free_launch: "Free launch",
          promotional: "Promotional pricing",
          standard: "Standard pricing",
        },
        freeLaunchEndsAt: "Free-launch end date (optional)",
        autoAdvance: "Automatically switch to promotional when the free launch ends",
        pricesTitle: "Prices (cents €)",
        promotional: "Promotional",
        standard: "Standard",
        monthly: "Monthly",
        sixMonth: "6 months",
        yearly: "12 months",
        currency: "Currency",
        save: "Save configuration",
        saved: "Configuration saved.",
        error: "Couldn't save.",
      },
      moderation: {
        title: "Moderation queue",
        subtitle: "Review community reports and apply the necessary actions.",
        empty: "No reports to review.",
        loadError: "Couldn't load reports.",
        filters: {
          status: "Status",
          reason: "Reason",
          all: "All",
        },
        table: {
          reported: "Reported",
          reason: "Reason",
          status: "Status",
          createdAt: "When",
          action: "Action",
          open: "Open",
          hasMessage: "With message context",
        },
        status: {
          pending: "Pending",
          reviewing: "Reviewing",
          resolved: "Resolved",
          dismissed: "Dismissed",
        },
        action: {
          no_action: "No action",
          warning: "Warning",
          suspend: "Suspend",
          ban: "Ban",
          remove_content: "Remove content",
        },
        detail: {
          title: "Report detail",
          reporter: "Reporter",
          reported: "Reported user",
          reason: "Reason",
          description: "Description",
          matchContext: "Related match",
          messageContext: "Related message",
          adminNotesLabel: "Internal notes (admin only)",
          adminNotesPlaceholder: "Notes visible only to administrators.",
          statusLabel: "Status",
          actionLabel: "Moderation action",
          actionReasonLabel: "Reason for the action",
          save: "Save changes",
          saved: "Changes saved.",
          saveError: "Couldn't save changes.",
          noMessageContext: "No related message",
          noAction: "No action applied",
          currentAction: "Current action",
        },
      },
      audit: {
        title: "Audit log",
        subtitle: "All sensitive administrator actions.",
        empty: "No audit entries.",
        columns: {
          when: "When",
          admin: "Admin",
          action: "Action",
          target: "Target",
          details: "Details",
        },
      },
    },
    notifications: {
      title: "Notifications",
      empty: "No notifications.",
      markAllRead: "Mark all as read",
      matchWith: "New match with {name}",
      newMessageFrom: "New message from {name}",
      justNow: "Just now",
      minutesAgo: "{n} minutes ago",
      hoursAgo: "{n} hours ago",
      daysAgo: "{n} days ago",
      unreadBadge: "unread",
    },
    comingSoon: {
      badge: "Coming soon",
      title: "Available soon.",
      body: "We're finishing this section. You'll be able to use it here shortly.",
    },
    profile: {
      title: "My profile",
      accountId: "Account ID",
      language: "Preferred language",
      country: "Country",
      city: "City",
      role: "Role",
      status: "Account status",
      emailVerified: "Email verified",
      membership: "Membership",
      complete: "Profile complete",
    },
    settings: {
      title: "Settings",
      language: "Language",
      logout: "Sign out",
    },
  },
};

export default en;
